/**
 * GovServiceReportsPage — `/government/reports`.
 *
 * Backed by `GET /v1/government/stats/reports?agency&from&to`. KPI tiles
 * pull from the bundle (revenue, avg processing, satisfaction). The per-agency
 * breakdown table includes outlets and feedback counts.
 */
import { type JSX, useState } from 'react';
import { BarChart3, Clock, DollarSign, Star } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { getReports } from '../../lib/api/government';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

const AGENCIES = ['', 'NIMC', 'NPC', 'FRSC', 'FIRS', 'INEC'];

function fmtNgn(amount: string | undefined): string {
  if (!amount) return '—';
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  return `NGN ${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** YYYY-MM → ISO start/end. */
function monthRange(yyyymm: string | ''): { from: string; to: string } | null {
  if (!yyyymm) return null;
  const [y, m] = yyyymm.split('-').map((s) => Number.parseInt(s, 10));
  if (y === undefined || m === undefined || !Number.isFinite(y) || !Number.isFinite(m)) return null;
  const from = new Date(y, m - 1, 1, 0, 0, 0).toISOString();
  const to = new Date(y, m, 1, 0, 0, 0).toISOString();
  return { from, to };
}

export function GovServiceReportsPage(): JSX.Element {
  const [agency, setAgency] = useState<string>('');
  const [period, setPeriod] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const range = monthRange(period);

  const reports = useQuery({
    queryKey: ['gov', 'reports', agency, period],
    queryFn: () =>
      getReports({
        ...(agency ? { agency } : {}),
        ...(range ? { from: range.from, to: range.to } : {}),
      }),
    retry: false,
  });

  const denied =
    reports.isError && reports.error instanceof ApiError && (reports.error.status === 401 || reports.error.status === 403);

  // Aggregate KPI tiles across all returned agencies.
  const all = reports.data?.agencies ?? [];
  const totalRequests = reports.data?.total.requests ?? 0;
  const totalRevenue = reports.data?.total.revenue;
  const avgProcessing =
    all.length === 0
      ? 0
      : Math.round((all.reduce((acc, a) => acc + a.avgProcessingHours, 0) / all.length) * 100) / 100;
  const satisfactionRows = all.filter((a) => a.satisfactionAverage !== null);
  const satisfaction =
    satisfactionRows.length === 0
      ? null
      : Math.round(
          (satisfactionRows.reduce((acc, a) => acc + (a.satisfactionAverage ?? 0), 0) / satisfactionRows.length) * 100,
        ) / 100;
  const satisfactionResponses = all.reduce((acc, a) => acc + a.satisfactionResponses, 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Government service reports" description="Performance analytics by agency." />

      <div className="bg-white border border-border rounded-xl p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Agency</label>
            <select
              data-testid="reports-agency"
              value={agency}
              onChange={(e) => setAgency(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            >
              <option value="">All agencies</option>
              {AGENCIES.filter(Boolean).map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Period</label>
            <input
              data-testid="reports-period"
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Outlets in scope</label>
            <p className="px-3 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg border border-border" data-testid="reports-outlet-count">
              {all.reduce((acc, a) => acc + a.outlets.length, 0)} outlet(s)
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total requests"
          value={reports.isPending ? '—' : String(totalRequests)}
          hint="Across selected window"
          icon={BarChart3}
        />
        <KPICard
          title="Revenue"
          value={reports.isPending ? '—' : fmtNgn(totalRevenue)}
          hint="Agency + NIPOST fees"
          icon={DollarSign}
          tone="success"
        />
        <KPICard
          title="Avg processing"
          value={reports.isPending ? '—' : `${avgProcessing} h`}
          hint="Mean across agencies"
          icon={Clock}
        />
        <KPICard
          title="Satisfaction"
          value={reports.isPending ? '—' : satisfaction === null ? '—' : `${satisfaction.toFixed(2)}/5`}
          hint={`${satisfactionResponses} feedback response(s)`}
          icon={Star}
        />
      </div>

      {denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Government/readStats</code>.
        </DeferredNotice>
      ) : reports.isError ? (
        <DeferredNotice title="Service unreachable">{(reports.error as Error).message}</DeferredNotice>
      ) : (
        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Per-agency breakdown</h2>
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Agency</th>
                  <th className="text-right px-4 py-2">Requests</th>
                  <th className="text-right px-4 py-2">Completed</th>
                  <th className="text-right px-4 py-2">Rejected</th>
                  <th className="text-right px-4 py-2">Revenue</th>
                  <th className="text-right px-4 py-2">Avg TAT (h)</th>
                  <th className="text-right px-4 py-2">CSAT</th>
                  <th className="text-right px-4 py-2">Outlets</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {all.map((row) => (
                  <tr key={row.agency} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-xs font-medium">{row.agency}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono">{row.totalRequests}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono">{row.completed}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono">{row.rejected}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono">{fmtNgn(row.revenue)}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono">{row.avgProcessingHours}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono">
                      {row.satisfactionAverage === null ? '—' : `${row.satisfactionAverage.toFixed(2)}`}
                      <span className="text-muted text-[10px]"> ({row.satisfactionResponses})</span>
                    </td>
                    <td className="px-4 py-2 text-xs text-right font-mono">{row.outlets.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {all.length === 0 ? (
              <p className="p-8 text-center text-sm text-muted">No requests in this window.</p>
            ) : null}
          </div>
        </section>
      )}
    </div>
  );
}
